import { notifications } from '@mantine/notifications';
import { canOpenSuitabilityFlow } from 'src/suitability-report/prefill';
import {
  ProposalOwner,
  ProposedActionStatus,
  SuitabilityReportProposedAction,
} from '../chat/types';
import { settledSuitabilityDecision } from './settledSuitabilityDecision';

type Args = {
  /** The proposals the server lists, which know their owner before a streamed card does. */
  proposals: SuitabilityReportProposedAction[];
  hostClientId: string | null;
  fetchProposal: (proposalId: string) => Promise<SuitabilityReportProposedAction | null>;
  markDecided: (
    widgetMessageId: string,
    proposalId: string,
    status: ProposedActionStatus,
    failureReason: string | null,
    owner: ProposalOwner,
  ) => void;
  recordOrphan: (
    proposalId: string,
    status: ProposedActionStatus,
    failureReason: string | null,
    owner: ProposalOwner,
  ) => void;
};

/**
 * The two decisions around approving a suitability report that are not about the request itself:
 * whether approving may start at all, and what a 409 means.
 */
export function useSuitabilityDecisionGuards({
  proposals,
  hostClientId,
  fetchProposal,
  markDecided,
  recordOrphan,
}: Args) {
  // Approving files the documents and tells Siesta the adviser is reviewing, so it must not
  // happen for a household proposal there is no client to open under.
  const canApprove = (proposal: SuitabilityReportProposedAction): boolean => {
    const owner = proposals.find(listed => listed.proposalId === proposal.proposalId) ?? proposal;
    if (canOpenSuitabilityFlow(owner, hostClientId)) return true;
    notifications.show({
      title: 'Open a client to review this report',
      message:
        'Go to one of the household’s clients, then choose Review report again. Nothing has been approved yet.',
    });
    return false;
  };

  // The decision already landed (a lost response, a second tab), so the card shows what the
  // server holds instead of assuming the proposal expired and hiding its reports link.
  const settleConflict = async (widgetMessageId: string | null, proposalId: string) => {
    const settled = await settledSuitabilityDecision(fetchProposal, proposalId);
    if (settled === 'in-progress') {
      notifications.show({
        title: 'A decision is already in progress',
        message: 'Another tab is deciding this proposal. Try again in a moment.',
      });
      return;
    }
    if (!settled) {
      notifications.show({
        title: 'Could not submit your decision',
        message: 'Something went wrong — please try again.',
        color: 'red',
      });
      return;
    }
    if (widgetMessageId) {
      markDecided(
        widgetMessageId,
        proposalId,
        settled.status,
        settled.failureReason,
        settled.owner,
      );
    } else {
      recordOrphan(proposalId, settled.status, settled.failureReason, settled.owner);
    }
    notifications.show({
      title: 'This proposal was already decided',
      message: 'The card now shows what happened.',
    });
  };

  return { canApprove, settleConflict };
}
